import { LucideIcon, MoreHorizontal } from "lucide-react";
import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import "./styles.scss";

export type TableRowActionIcon = LucideIcon;

export interface TableRowActionItem {
  key: string;
  label: string;
  icon: TableRowActionIcon;
  onClick?: () => void;
  href?: string;
  target?: string;
  rel?: string;
  isDanger?: boolean;
}

export interface TableRowActionsProps {
  actions: TableRowActionItem[];
  isMenuOpen?: boolean;
  onToggleMenu?: () => void;
  moreActionsLabel?: string;
}

const renderIconAction = (
  action: TableRowActionItem,
  variant: "inline" | "menu-item",
  onAfterClick?: () => void
) => {
  const Icon = action.icon;
  const className =
    variant === "inline"
      ? `table-row-action-btn${action.isDanger ? " is-danger" : ""}`
      : `table-row-actions-dropdown-item${action.isDanger ? " is-danger" : ""}`;

  const handleClick = () => {
    action.onClick?.();
    onAfterClick?.();
  };

  const content =
    variant === "inline" ? (
      <Icon size={16} aria-hidden={true} />
    ) : (
      <>
        <Icon size={16} aria-hidden={true} />
        <span>{action.label}</span>
      </>
    );

  if (action.href) {
    return (
      <a
        key={action.key}
        href={action.href}
        target={action.target}
        rel={action.rel}
        className={className}
        title={variant === "inline" ? action.label : undefined}
        aria-label={variant === "inline" ? action.label : undefined}
        onClick={handleClick}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      key={action.key}
      type="button"
      className={className}
      title={variant === "inline" ? action.label : undefined}
      aria-label={variant === "inline" ? action.label : undefined}
      onClick={handleClick}
    >
      {content}
    </button>
  );
};

interface RowActionsDropdownProps {
  items: TableRowActionItem[];
  triggerRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
}

const RowActionsDropdown: React.FunctionComponent<RowActionsDropdownProps> = ({
  items,
  triggerRef,
  onClose,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({
    visibility: "hidden",
  });

  useLayoutEffect(() => {
    const recompute = () => {
      const trigger = triggerRef.current;
      const dropdown = dropdownRef.current;
      if (!trigger || !dropdown) return;

      const triggerRect = trigger.getBoundingClientRect();
      const dropdownHeight = dropdown.offsetHeight;
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const openUpward =
        spaceBelow < dropdownHeight + 8 && triggerRect.top > dropdownHeight + 8;

      setStyle({
        position: "fixed",
        right: window.innerWidth - triggerRect.right,
        ...(openUpward
          ? { bottom: window.innerHeight - triggerRect.top + 4 }
          : { top: triggerRect.bottom + 4 }),
      });
    };

    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [triggerRef]);

  useLayoutEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return;
      }
      onClose();
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [triggerRef, onClose]);

  return createPortal(
    <div
      ref={dropdownRef}
      className="table-row-actions-dropdown"
      style={style}
    >
      {items.map((action) => renderIconAction(action, "menu-item", onClose))}
    </div>,
    document.body
  );
};

export const TableRowActions: React.FunctionComponent<TableRowActionsProps> = ({
  actions,
  isMenuOpen = false,
  onToggleMenu,
  moreActionsLabel = "操作メニューを開く",
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  if (actions.length === 0) {
    return <span className="table-row-actions-empty">-</span>;
  }

  if (actions.length <= 2) {
    return (
      <div
        className="table-row-actions"
        onClick={(event) => event.stopPropagation()}
      >
        {actions.map((action) => renderIconAction(action, "inline"))}
      </div>
    );
  }

  const [primaryAction, ...overflowActions] = actions;

  return (
    <div
      className="table-row-actions"
      onClick={(event) => event.stopPropagation()}
    >
      {renderIconAction(primaryAction, "inline")}
      <div className="table-row-actions-menu" ref={menuRef}>
        <button
          type="button"
          className="table-row-actions-menu-trigger"
          aria-label={moreActionsLabel}
          aria-expanded={isMenuOpen}
          onClick={onToggleMenu}
        >
          <MoreHorizontal size={20} aria-hidden="true" />
        </button>
        {isMenuOpen && onToggleMenu && (
          <RowActionsDropdown
            items={overflowActions}
            triggerRef={menuRef}
            onClose={onToggleMenu}
          />
        )}
      </div>
    </div>
  );
};

export default TableRowActions;
